import { gql, useMutation } from '@apollo/client';
import { CircularProgress } from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import Paper from '@material-ui/core/Paper';
import Snackbar from '@material-ui/core/Snackbar';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import { graphql, useStaticQuery } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import React from 'react';
import useTemplateStyles from '../styles/templateStyle';
import { defaultLocation, Languages } from '../utils/getAllSupportedLanguages';
import { AuthUserContext } from '../contexts/AuthUserContext';
import { LocationLanguageContext } from '../contexts/LocationLanguageContext';
import { NovadeLiteUrlContext } from '../contexts/NovadeLiteUrlContext';
import {
  AuthUserContextType,
  LocationLanguageContextType,
  LogInResponse,
  NovadeLiteUrlContextType,
  NovadeLiteUser,
  StrapiTemplate,
  StrapiTemplateSearchResult,
  Template,
  TemplateCategory
} from '../typings';
import { exportToPDF } from '../utils/exportPDF';
import { getTemplateCategoryByLocale } from '../utils/getFilterOptions';
import { getAttachmentData, getEmailValues, sendEmail, setEmailValues } from '../utils/sendStrapiEmail';
import { setGTMEvent } from '../utils/setGAEvent';
import { getTemplateValues, updateTemplateURL, updateTemplateCountryCode, getSortedTemplate } from '../utils/templateValues';
import DesktopFormContainer from './DesktopFormContainer';
import LoginDialog from './LoginDialog';
import MobileFormContainer from './MobileFormContainer';
import PardotEmailDialog from './PardotEmailDialog';
import Seo from './Seo';
import ShareDialog from './ShareDialog';
import WorkspaceDialog from './WorkspaceDialog';
import TemplateSeoContent from './Template/TemplateSeoContent';

interface TemplatePreviewDialogProps {
  open: boolean;
  template: StrapiTemplate | StrapiTemplateSearchResult;
  onDialogClose: (value?: any) => void;
  isMobile: boolean;
}

const TemplatePreviewDialog: React.FC<TemplatePreviewDialogProps> = (templatePreviewDialogProps) => {
  const data = useStaticQuery(
    graphql`
      query AllTemplatesQuery {
        allStrapiTemplatePage {
          nodes {
            PublishedDateLabel
            AuthorLabel
            SaveToNovadeLiteLabel
            locale
            WebPreviewLabel
            MobilePreviewLabel
            DownloadPDFLabel
            SuccessMessage
            ErrorMessage
            ShareLabel
            WorkspaceLabel
            WorkspaceButton
          }
        }
      }
    `
  );

  const locationLanguage = React.useContext<LocationLanguageContextType | null>(LocationLanguageContext);
  const language: string = locationLanguage?.language ?? Languages.English;
  const country = locationLanguage?.country ?? defaultLocation;

  const templatePage = data.allStrapiTemplatePage.nodes.find((x: any) => x.locale === language);

  const classes = useTemplateStyles();
  const { open, template, onDialogClose } = templatePreviewDialogProps;
  const templateDetails: Template = getSortedTemplate({ ...template.Template, ...{ id: template.id } });
  const templateLanguage = template.template_language || Languages.English;
  const [openWorkspace, setOpenWorkspace] = React.useState(false);
  const [openSnackbar, setOpenSnackbar] = React.useState(false);
  const [openLogin, setOpenLogin] = React.useState(false);
  const [openEmail, setOpenEmail] = React.useState(false);
  const [downloadType, setDownloadType] = React.useState('');
  const [openSharePopup, setOpenSharePopup] = React.useState(false);
  const [snackBarMsg, setSnackBarMsg] = React.useState('');
  const authUser = React.useContext<AuthUserContextType | null>(AuthUserContext);

  let countries: TemplateCategory[] | null = [];
  if (template.countries) {
    countries = getTemplateCategoryByLocale(template.countries, language);
  }

  // update to unique template url when preview is opened
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  if (open && !pathname.includes('/template')) {
    updateTemplateURL({ template: template.Title }, template.Title, `/template/${template.Slug}`);
  }

  const contextUrl = React.useContext<NovadeLiteUrlContextType | null>(NovadeLiteUrlContext);
  const databaseMaps = contextUrl ? authUser?.databasesMap[contextUrl.novadeLiteUrl] || {} : {};

  const exportPDF = (userData: NovadeLiteUser) => {
    const templateValues = getTemplateValues();
    updateTemplateCountryCode(country.locationCode);
    const pdfEndpoint = `${contextUrl?.novadeLiteUrl}/portal-template-pdf`;
    exportToPDF(pdfEndpoint, templateValues).then((fileData) => {
      const attachmentData = getAttachmentData(fileData.fileContent, fileData.fileName, fileData.fileType);
      setEmailValues(userData, language, template, attachmentData!);
      const emailMsg = getEmailValues();
      sendEmail(emailMsg);
    });
    setGTMEvent('Download Template as PDF', 'download_PDF', templateValues.name);
  };

  // to save directly to Novade Lite without opening Workspace Dialog if user only has 1 workspace
  const UPSERT_TEMPLATE_DATE = gql`
    mutation upsertTemplateData($template: TemplatePortalInput!) {
      savePortalTemplateToWorkspace(template: $template)
    }
  `;
  const [upsertTemplateData, { loading }] = useMutation<{ savePortalTemplateToWorkspace: any }, { template: Template }>(
    UPSERT_TEMPLATE_DATE,
    {
      variables: {
        template: templateDetails
      },
      context: {
        uri: `${contextUrl?.novadeLiteUrl}/graphql`,
        headers: {
          Authorization: Object.values(databaseMaps).length !== 0 ? `Bearer ${Object.values(databaseMaps)[0].token}` : 'No Auth'
        }
      },
      onCompleted({ savePortalTemplateToWorkspace }) {
        if (savePortalTemplateToWorkspace) {
          setGTMEvent('Save template to Novade Lite workspace', 'save_To_Novade_Lite', templateDetails.name);
          setSnackBarMsg(templatePage.SuccessMessage);
          setOpenSnackbar(true);
        }
      },
      onError({ graphQLErrors, networkError }) {
        if (graphQLErrors || networkError) {
          setSnackBarMsg(templatePage.ErrorMessage);
          setOpenSnackbar(true);
        }
      }
    }
  );

  const handleSnackbarClose = (_: React.SyntheticEvent | React.MouseEvent, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  const handleWorkspaceDialogClose = (isSuccessfulSave: any | undefined) => {
    setOpenWorkspace(false);
    if (typeof isSuccessfulSave !== 'undefined') {
      if (isSuccessfulSave) {
        setSnackBarMsg(templatePage.SuccessMessage);
      } else {
        setSnackBarMsg(templatePage.ErrorMessage);
      }
      setOpenSnackbar(true);
    }
  };

  const handleLoginDialogClose = (value: LogInResponse | undefined) => {
    if (value) {
      authUser?.updateUser({
        email: value.email,
        displayName: value.userDisplayName
      });
      authUser?.updateDatabasesMap(value.databases);
    }
    setOpenLogin(false);
  };

  const openWorkspaceDialog = () => {
    setOpenWorkspace(true);
  };

  const openLoginDialog = () => {
    setOpenLogin(true);
  };

  const handlePreviewClose = () => {
    onDialogClose();
  };

  const onSaveToNovadeLite = () => {
    if (Object.values(databaseMaps).length === 1) {
      upsertTemplateData();
    } else if (Object.values(databaseMaps).length > 1) {
      openWorkspaceDialog();
    } else {
      openLoginDialog();
    }
  };

  const handleDownload = (type: string) => {
    // no need for email request if user already logged in with NL account
    if (Object.values(databaseMaps).length >= 1) {
      if (type === 'pdf') {
        exportPDF(authUser?.user!);
      }
    } else {
      setDownloadType(type);
      setOpenEmail(true);
    }
  };
  const handleEmailDialogClose = (isSuccessfulSubmit: boolean | undefined, inputEmail: string | undefined) => {
    setOpenEmail(false);
    if (isSuccessfulSubmit) {
      const userData: NovadeLiteUser = {
        email: inputEmail!,
        displayName: '' // guest
      };
      if (downloadType === 'pdf') {
        exportPDF(userData);
      }
    }
  };
  const openShareDialog = () => {
    setOpenSharePopup(true);
  };

  const handleShareDialogClose = () => {
    setOpenSharePopup(false);
  };
  if (templatePage) {
    return (
      <>
        <Seo
          title={template.Title}
          description={template.Description}
          keywords={template.Keywords}
          pathname={`/template/${template.Slug}`}
        />
        <Dialog
          fullScreen
          open={open}
          onClose={handlePreviewClose}
          className={classes.templatePreview}
          BackdropProps={{ style: { cursor: 'pointer', backgroundColor: 'rgba(0, 0, 0, 0.85)' } }}
          PaperProps={{ classes: { root: classes.dialogPaper } }}
        >
          <div style={{ position: 'fixed', top: '10px', right: '10px' }}>
            <IconButton aria-label='close' onClick={handlePreviewClose} className={classes.closeButton}>
              <CloseIcon />
            </IconButton>
          </div>
          <Grid container style={{ backgroundColor: 'transparent', paddingLeft: '2em' }}>
            <Grid item xs={12} md={12}>
              <div className={classes.dialogTitle}>
                <Typography variant='h6'>{template.Title}</Typography>
                <Typography variant='body2'>
                  {templatePage.AuthorLabel} {template.Author} <span> • </span> {templatePage.PublishedDateLabel}{' '}
                  {new Date(template.published_at).toLocaleDateString()}
                </Typography>
                <Typography variant='body2' className={classes.descriptionContainer}>
                  {template.Description}
                </Typography>
                <Typography variant='body2' style={{ display: 'none' }}>
                  {countries?.map((tCountry, i, arr) => `${tCountry.displayName}${i !== arr.length - 1 ? ' • ' : ''}`)}
                </Typography>
              </div>
              <Grid container className={classes.dialogContainer}>
                <Grid item xs={12} md={8} className={classes.previewGrid}>
                  <Paper className={classes.previewCard}>
                    <div className={classes.previewHeader} style={{ position: 'sticky', top: '0px', zIndex: 2 }}>
                      <StaticImage
                        layout='constrained'
                        placeholder='tracedSVG'
                        src='../assets/TemplatePreview/desktop_windows.svg'
                        alt='Web Preview'
                      />
                      <Typography variant='h6'>{templatePage.WebPreviewLabel}</Typography>
                    </div>
                    <div style={{ backgroundColor: '#fff', borderBottomRightRadius: '4px', borderBottomLeftRadius: '4px' }}>
                      <DesktopFormContainer templateDetails={templateDetails} templateLanguage={templateLanguage} />
                    </div>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4} className={classes.previewGrid}>
                  <Paper className={classes.previewCard} style={{ position: 'sticky', top: '0px' }}>
                    <div className={classes.previewHeader}>
                      <StaticImage
                        layout='constrained'
                        placeholder='tracedSVG'
                        src='../assets/TemplatePreview/smartphone.svg'
                        alt='Mobile Preview'
                      />
                      <Typography variant='h6'>{templatePage.MobilePreviewLabel}</Typography>
                    </div>
                    <div className={classes.mobileForm}>
                      <div style={{ margin: '2rem auto', display: 'inline-block' }}>
                        <MobileFormContainer templateDetails={templateDetails} templateLanguage={templateLanguage} />
                      </div>
                    </div>
                  </Paper>
                </Grid>
              </Grid>
            </Grid>
            <TemplateSeoContent seoContent={template.SeoContent} />
            <div>
              <List style={{ position: 'fixed', top: '170px', right: '15px' }}>
                <ListItem button onClick={openShareDialog} className={classes.buttonList}>
                  <ListItemIcon>
                    <div className={classes.buttonCircle}>
                      <StaticImage
                        width={24}
                        layout='constrained'
                        placeholder='tracedSVG'
                        src='../assets/TemplatePreview/outlined_share.svg'
                        alt='Share'
                      />
                    </div>
                  </ListItemIcon>
                  <Typography variant='body2' className={classes.listItemText}>
                    {templatePage.ShareLabel}
                  </Typography>
                </ListItem>
                <ListItem button onClick={() => handleDownload('pdf')} className={classes.buttonList}>
                  <ListItemIcon>
                    <div className={classes.buttonCircle}>
                      <StaticImage
                        width={24}
                        layout='constrained'
                        placeholder='tracedSVG'
                        src='../assets/TemplatePreview/pdf.svg'
                        alt='Download PDF'
                      />
                    </div>
                  </ListItemIcon>
                  <Typography variant='body2' className={classes.listItemText}>
                    {templatePage.DownloadPDFLabel}
                  </Typography>
                </ListItem>
                <ListItem button onClick={() => onSaveToNovadeLite()} disabled={loading} className={classes.buttonList}>
                  <ListItemIcon>
                    <div className={classes.buttonCircle} style={{ backgroundColor: '#2b5b94' }}>
                      <StaticImage
                        width={24}
                        layout='constrained'
                        placeholder='tracedSVG'
                        src='../assets/TemplatePreview/sync_alt_white.svg'
                        alt='Save to Novade Lite'
                      />
                    </div>
                  </ListItemIcon>
                  {loading && <CircularProgress style={{ height: '1rem', width: '1rem', color: 'white' }} />}
                  <Typography variant='body2' className={classes.listItemText}>
                    {templatePage.SaveToNovadeLiteLabel}
                  </Typography>
                </ListItem>
              </List>
            </div>
          </Grid>
          <Snackbar
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center'
            }}
            open={openSnackbar}
            onClose={handleSnackbarClose}
            autoHideDuration={5000}
            message={snackBarMsg}
          />
          <WorkspaceDialog
            open={openWorkspace}
            onDialogClose={handleWorkspaceDialogClose}
            template={templateDetails}
            selectLabel={templatePage.WorkspaceLabel}
            btnLabel={templatePage.WorkspaceButton}
          />
          <LoginDialog open={openLogin} onDialogClose={handleLoginDialogClose} />
          <PardotEmailDialog open={openEmail} templateTitle={template.Title} onDialogClose={handleEmailDialogClose} />
          <ShareDialog open={openSharePopup} onDialogClose={handleShareDialogClose} title={template.Title} />
        </Dialog>
      </>
    );
  }
  return null;
};
export default TemplatePreviewDialog;
